import React from 'react';
import { Modal, Button, Row, Col, Card } from 'react-bootstrap';
import { X, InfoCircle, Gear, CheckCircle, ExclamationTriangle } from 'react-bootstrap-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLayerGroup, faWarehouse, faFlask } from '@fortawesome/free-solid-svg-icons';
import './IngredientHelpModal.scss';

interface IngredientHelpModalProps {
  show: boolean;
  handleClose: () => void;
}

const IngredientHelpModal: React.FC<IngredientHelpModalProps> = ({ show, handleClose }) => {
  return (
    <Modal show={show} onHide={handleClose} className="ingredient-help-modal" size="lg">
      <Modal.Header className="help-modal-header">
        <div className="help-header-content">
          <InfoCircle className="help-icon" />
          <Modal.Title className="help-modal-title">¿Cómo funciona la creación de ingredientes?</Modal.Title>
        </div>
        <Button variant="none" onClick={handleClose} className="help-close-btn">
          <X size={24} />
        </Button>
      </Modal.Header>
      
      <Modal.Body className="help-modal-body">
        <div className="help-intro">
          <p>
            Los ingredientes son la base de tu restaurante. Aquí te explicamos paso a paso cómo crear y configurar 
            ingredientes de manera inteligente para tu negocio.
          </p>
        </div>

        <div className="help-section">
          <h4><InfoCircle className="section-icon" /> Información Básica</h4>
          <Card className="help-card">
            <Card.Body>
              <Row>
                <Col md={6}>
                  <div className="help-item">
                    <strong>Nombre del Ingrediente</strong>
                    <p>El nombre que identificará este ingrediente en tu inventario. Debe ser único y descriptivo.</p>
                    <div className="example">
                      <strong>Ejemplo:</strong> "Tomate Fresco", "Queso Mozzarella", "Aceite de Oliva"
                    </div>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="help-item">
                    <strong>Unidad de Medida</strong>
                    <p>La unidad principal en la que manejarás este ingrediente (kilogramos, litros, unidades, etc.).</p>
                    <div className="example">
                      <strong>Ejemplo:</strong> Tomate → Kilogramos, Aceite → Litros, Huevos → Unidades
                    </div>
                  </div>
                </Col>
              </Row>
              <div className="help-item">
                <strong>Descripción</strong>
                <p>Información adicional sobre el ingrediente que ayude a identificarlo mejor (marca, características, etc.).</p>
              </div>
            </Card.Body>
          </Card>
        </div>

        <div className="help-section">
          <h4><FontAwesomeIcon icon={faLayerGroup} className="section-icon" /> Ingredientes Compuestos</h4>
          <Card className="help-card">
            <Card.Body>
              <div className="toggle-explanation">
                <div className="toggle-visual">
                  <FontAwesomeIcon icon={faLayerGroup} className="config-icon" />
                  <span className="toggle-label">Compuesto por otros sub-ingredientes</span>
                </div>
                <div className="toggle-description">
                  <p><strong>¿Cuándo activar esto?</strong></p>
                  <p>Activa esta opción cuando tu ingrediente esté hecho de otros ingredientes más básicos.</p>
                </div>
              </div>
              
              <Row className="help-examples">
                <Col md={6}>
                  <div className="example-case good">
                    <CheckCircle className="example-icon" />
                    <strong>Casos donde SÍ usar:</strong>
                    <ul>
                      <li><strong>Salsa de tomate:</strong> Tomate + Cebolla + Especias</li>
                      <li><strong>Masa para pizza:</strong> Harina + Agua + Levadura + Sal</li>
                      <li><strong>Aderezo especial:</strong> Mayonesa + Mostaza + Hierbas</li>
                      <li><strong>Mezcla de especias:</strong> Sal + Pimienta + Oregano</li>
                    </ul>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="example-case bad">
                    <X className="example-icon" />
                    <strong>Casos donde NO usar:</strong>
                    <ul>
                      <li><strong>Tomate fresco:</strong> Es un ingrediente básico</li>
                      <li><strong>Queso mozzarella:</strong> Lo compras tal como es</li>
                      <li><strong>Aceite de oliva:</strong> Producto final que compras</li>
                      <li><strong>Sal:</strong> Ingrediente básico y simple</li>
                    </ul>
                  </div>
                </Col>
              </Row>

              <div className="important-note">
                <ExclamationTriangle className="note-icon" />
                <strong>Importante:</strong> Si activas esto, podrás agregar los ingredientes que lo componen y sus cantidades. 
                Esto es útil para calcular costos y controlar inventario de manera precisa.
              </div>
            </Card.Body>
          </Card>
        </div>

        <div className="help-section">
          <h4><FontAwesomeIcon icon={faWarehouse} className="section-icon" /> Control de Stock</h4>
          <Card className="help-card">
            <Card.Body>
              <div className="toggle-explanation">
                <div className="toggle-visual">
                  <FontAwesomeIcon icon={faWarehouse} className="config-icon" />
                  <span className="toggle-label">Activar control de stock</span>
                </div>
                <div className="toggle-description">
                  <p><strong>¿Cuándo activar esto?</strong></p>
                  <p>Activa esta opción cuando quieras que el sistema controle automáticamente cuánto tienes de este ingrediente.</p>
                </div>
              </div>

              <div className="stock-benefits">
                <h5>Beneficios del control de stock:</h5>
                <Row>
                  <Col md={6}>
                    <ul className="benefits-list">
                      <li>📊 <strong>Saber cuánto tienes:</strong> El sistema te dice exactamente cuánto queda</li>
                      <li>⚠️ <strong>Alertas automáticas:</strong> Te avisa cuando se está acabando</li>
                      <li>📉 <strong>Descuento automático:</strong> Reduce la cantidad cuando vendes algo</li>
                    </ul>
                  </Col>
                  <Col md={6}>
                    <ul className="benefits-list">
                      <li>📍 <strong>Ubicación clara:</strong> Sabes dónde está almacenado</li>
                      <li>💰 <strong>Evita pérdidas:</strong> No te quedas sin ingredientes importantes</li>
                      <li>📈 <strong>Mejor planificación:</strong> Compras lo justo y necesario</li>
                    </ul>
                  </Col>
                </Row>
              </div>

              <div className="stock-config-explanation">
                <h5>¿Qué información necesitas proporcionar?</h5>
                <Row>
                  <Col md={6}>
                    <div className="config-field">
                      <strong>Unidad de Medida del Stock</strong>
                      <p>En qué unidad vas a controlar el inventario (puede ser diferente a la unidad de la receta).</p>
                      <div className="field-example">
                        <strong>Ejemplo:</strong> Compras aceite en litros, pero lo usas en mililitros en las recetas.
                      </div>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="config-field">
                      <strong>Stock Actual</strong>
                      <p>¿Cuánto tienes ahora mismo de este ingrediente?</p>
                      <div className="field-example">
                        <strong>Ejemplo:</strong> Tengo 5 kilogramos de tomate en el almacén.
                      </div>
                    </div>
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <div className="config-field">
                      <strong>Stock Mínimo</strong>
                      <p>¿Cuál es la cantidad mínima que quieres tener? El sistema te alertará cuando llegues a este punto.</p>
                      <div className="field-example">
                        <strong>Ejemplo:</strong> Quiero que me avise cuando tenga menos de 2 kilogramos de tomate.
                      </div>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="config-field">
                      <strong>Almacenamiento</strong>
                      <p>¿Dónde guardas este ingrediente? (Refrigerador, congelador, despensa, etc.)</p>
                      <div className="field-example">
                        <strong>Ejemplo:</strong> Los tomates van en el refrigerador, la harina en la despensa.
                      </div>
                    </div>
                  </Col>
                </Row>
              </div>
            </Card.Body>
          </Card>
        </div>

        <div className="help-section">
          <h4><FontAwesomeIcon icon={faFlask} className="section-icon" /> Ingredientes que Requieren Producción</h4>
          <Card className="help-card">
            <Card.Body>
              <div className="production-explanation">
                <p>
                  <strong>Esta opción aparece solo cuando:</strong> Has activado "Compuesto por sub-ingredientes" 
                  Y también has activado "Control de stock".
                </p>
              </div>

              <div className="production-scenarios">
                <h5>¿Tu ingrediente necesita ser preparado antes de usarse?</h5>
                
                <Row>
                  <Col md={6}>
                    <div className="scenario-card needs-production">
                      <h6><CheckCircle className="scenario-icon" /> SÍ, necesita preparación</h6>
                      <p><strong>Ejemplo:</strong> Salsa de tomate casera</p>
                      <div className="scenario-flow">
                        <div className="flow-step">1. Compras: Tomate, cebolla, especias</div>
                        <div className="flow-arrow">↓</div>
                        <div className="flow-step">2. Preparas: 5 litros de salsa</div>
                        <div className="flow-arrow">↓</div>
                        <div className="flow-step">3. Almacenas: Los 5 litros listos para usar</div>
                        <div className="flow-arrow">↓</div>
                        <div className="flow-step">4. Vendes pizza: Se descuenta de los 5 litros</div>
                      </div>
                      <div className="scenario-note">
                        ℹ️ <strong>El sistema descuenta del stock preparado</strong> (los 5 litros), no de los ingredientes base.
                      </div>
                    </div>
                  </Col>
                  
                  <Col md={6}>
                    <div className="scenario-card no-production">
                      <h6><X className="scenario-icon" /> NO, se prepara al momento</h6>
                      <p><strong>Ejemplo:</strong> Ensalada mixta</p>
                      <div className="scenario-flow">
                        <div className="flow-step">1. Compras: Lechuga, tomate, cebolla</div>
                        <div className="flow-arrow">↓</div>
                        <div className="flow-step">2. Cliente pide ensalada</div>
                        <div className="flow-arrow">↓</div>
                        <div className="flow-step">3. Preparas en el momento</div>
                        <div className="flow-arrow">↓</div>
                        <div className="flow-step">4. Se descuenta: Lechuga, tomate, cebolla</div>
                      </div>
                      <div className="scenario-note">
                        ℹ️ <strong>El sistema descuenta directamente</strong> de los ingredientes base (lechuga, tomate, cebolla).
                      </div>
                    </div>
                  </Col>
                </Row>
              </div>

              <div className="production-decision-help">
                <ExclamationTriangle className="decision-icon" />
                <strong>¿Cómo decidir?</strong>
                <ul>
                  <li><strong>Elige "SÍ"</strong> si preparas grandes cantidades que luego almacenas y usas gradualmente</li>
                  <li><strong>Elige "NO"</strong> si mezclas los ingredientes frescos cada vez que los necesitas</li>
                </ul>
              </div>
            </Card.Body>
          </Card>
        </div>

        <div className="help-section">
          <h4><Gear className="section-icon" /> Combinaciones Típicas</h4>
          <Card className="help-card">
            <Card.Body>
              <Row>
                <Col md={6}>
                  <div className="combination-example">
                    <h6>🥗 Ingrediente Simple</h6>
                    <div className="combination-settings">
                      <div className="setting">✅ Control de stock: <strong>Activado</strong></div>
                      <div className="setting">❌ Compuesto: <strong>Desactivado</strong></div>
                    </div>
                    <p><strong>Ejemplo:</strong> Tomate fresco, Queso mozzarella, Aceite de oliva</p>
                  </div>
                  
                  <div className="combination-example">
                    <h6>🍝 Salsa que Preparas y Almacenas</h6>
                    <div className="combination-settings">
                      <div className="setting">✅ Control de stock: <strong>Activado</strong></div>
                      <div className="setting">✅ Compuesto: <strong>Activado</strong></div>
                      <div className="setting">✅ Requiere producción: <strong>SÍ</strong></div>
                    </div>
                    <p><strong>Ejemplo:</strong> Salsa boloñesa, Pesto casero</p>
                  </div>
                </Col>
                
                <Col md={6}>
                  <div className="combination-example">
                    <h6>🥪 Ingrediente sin Control</h6>
                    <div className="combination-settings">
                      <div className="setting">❌ Control de stock: <strong>Desactivado</strong></div>
                      <div className="setting">❌ Compuesto: <strong>Desactivado</strong></div>
                    </div>
                    <p><strong>Ejemplo:</strong> Especias que no quieres controlar</p>
                  </div>
                  
                  <div className="combination-example">
                    <h6>🥗 Mezcla Fresca</h6>
                    <div className="combination-settings">
                      <div className="setting">❌ Control de stock: <strong>Desactivado</strong></div>
                      <div className="setting">✅ Compuesto: <strong>Activado</strong></div>
                      <div className="setting">⚪ Requiere producción: <strong>NO aplica</strong></div>
                    </div>
                    <p><strong>Ejemplo:</strong> Ensalada mixta, Mezcla de especias</p>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </div>

        <div className="help-final-tips">
          <Card className="tips-card">
            <Card.Body>
              <h5><InfoCircle className="tips-icon" /> Consejos Finales</h5>
              <Row>
                <Col md={6}>
                  <ul className="tips-list">
                    <li>💡 <strong>Empieza simple:</strong> Crea primero ingredientes básicos sin composición</li>
                    <li>🎯 <strong>Activa stock solo en lo importante:</strong> No todo necesita control de inventario</li>
                    <li>📝 <strong>Usa descripciones claras:</strong> Te ayudarán a identificar ingredientes similares</li>
                  </ul>
                </Col>
                <Col md={6}>
                  <ul className="tips-list">
                    <li>🔄 <strong>Puedes modificar después:</strong> Siempre puedes cambiar la configuración más tarde</li>
                    <li>⚖️ <strong>Unidades consistentes:</strong> Usa las mismas unidades que manejas físicamente</li>
                    <li>🏪 <strong>Piensa en tu flujo:</strong> ¿Cómo realmente trabajas en tu cocina?</li>
                  </ul>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </div>
      </Modal.Body>
      
      <Modal.Footer className="help-modal-footer">
        <Button className="help-got-it-btn" onClick={handleClose}>
          ¡Entendido!
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default IngredientHelpModal;
